/**
 * /mfa-recovery/* — self-service reset cuando el usuario perdió app + códigos.
 *
 * Tres estados visuales:
 *   /mfa-recovery/request           formulario email + password
 *   /mfa-recovery/cancel?token=…    cancela una solicitud (link del email)
 *   /mfa-recovery/confirm?token=…   confirma el reset tras 72h
 */

import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Loader2, ShieldOff } from 'lucide-react'

import { mfaRecoveryApi } from '@/lib/api'
import { ROUTES, APP } from '@/lib/constants'
import { ThemeToggle } from '@/components/ui/theme-toggle'

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-app px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <a
            href={import.meta.env.VITE_APP_URL ?? 'https://taxor.co'}
            className="flex items-center gap-2"
          >
            <div
              aria-hidden
              className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-brand-600 font-mono text-sm font-bold text-white shadow-sm"
            >
              Tx
            </div>
            <span className="text-base font-semibold text-fg-primary">{APP.name}</span>
          </a>
          <ThemeToggle />
        </div>
        <div className="rounded-xl border border-border bg-bg-surface p-8 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  )
}

// ─── /mfa-recovery/request ────────────────────────────────────────

export function MfaRecoveryRequestPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState<{ message: string; notBefore: string } | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const r = await mfaRecoveryApi.request({ email: email.trim(), password })
      setDone({ message: r.message, notBefore: r.not_before })
    } finally {
      setLoading(false)
      setPassword('')
    }
  }

  if (done) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-tint">
            <CheckCircle2 className="h-6 w-6 text-success-fg" />
          </div>
          <h1 className="text-lg font-semibold text-fg-primary">Solicitud recibida</h1>
          <p className="text-sm text-fg-secondary">{done.message}</p>
          {done.notBefore && (
            <p className="text-xs text-fg-muted">
              Si la solicitud era tuya, podrás confirmarla a partir del{' '}
              <strong>{new Date(done.notBefore).toLocaleString('es-CO')}</strong>.
            </p>
          )}
          <Link
            to={ROUTES.LOGIN}
            className="mt-2 rounded-md bg-fg-primary px-4 py-2 text-sm font-medium text-bg-app hover:opacity-90"
          >
            Volver al login
          </Link>
        </div>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-warning-tint text-warning-fg">
          <ShieldOff className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-fg-primary">Perdí mi 2FA</h1>
          <p className="text-sm text-fg-secondary">Reset con espera de 72 horas</p>
        </div>
      </div>
      <p className="mt-4 text-sm text-fg-secondary">
        Si perdiste tu app de autenticación <em>y</em> los códigos de respaldo, puedes pedir
        un reset. Por seguridad bancaria, el reset toma 72 horas: durante ese periodo el
        dueño legítimo de la cuenta puede cancelarlo desde el correo.
      </p>
      <form onSubmit={onSubmit} className="mt-5 space-y-4">
        <div>
          <label htmlFor="r-email" className="block text-xs font-medium uppercase tracking-wider text-fg-muted">
            Correo electrónico
          </label>
          <input
            id="r-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        <div>
          <label htmlFor="r-pwd" className="block text-xs font-medium uppercase tracking-wider text-fg-muted">
            Contraseña actual
          </label>
          <input
            id="r-pwd"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email || !password}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Procesando…
            </>
          ) : (
            <>Enviar solicitud</>
          )}
        </button>
      </form>
      <p className="mt-6 border-t border-border pt-6 text-xs text-fg-muted">
        ¿Recordaste tu 2FA?{' '}
        <Link to={ROUTES.LOGIN} className="font-medium text-brand-600 hover:underline">
          Volver al login
        </Link>
      </p>
    </Shell>
  )
}

// ─── /mfa-recovery/confirm?token=… ────────────────────────────────

export function MfaRecoveryConfirmPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const [state, setState] = useState<'idle' | 'loading' | 'ok' | 'err'>(token ? 'idle' : 'err')
  const [message, setMessage] = useState<string>(
    token ? 'Confirma para deshabilitar tu 2FA.' : 'El enlace está incompleto.',
  )

  const onConfirm = async () => {
    setState('loading')
    try {
      const r = await mfaRecoveryApi.confirm(token)
      setMessage(r.message)
      setState('ok')
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setMessage(typeof detail === 'string' ? detail : 'No pudimos completar el reset.')
      setState('err')
    }
  }

  return (
    <Shell>
      {state === 'ok' ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle2 className="h-10 w-10 text-success-fg" />
          <h1 className="text-lg font-semibold text-fg-primary">2FA desactivada</h1>
          <p className="text-sm text-fg-secondary">{message}</p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
            className="mt-2 rounded-md bg-fg-primary px-4 py-2 text-sm font-medium text-bg-app hover:opacity-90"
          >
            Iniciar sesión
          </button>
        </div>
      ) : state === 'err' ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-10 w-10 text-error-fg" />
          <h1 className="text-lg font-semibold text-fg-primary">No pudimos confirmar</h1>
          <p className="text-sm text-fg-secondary">{message}</p>
          <Link to={ROUTES.MFA_RECOVERY_REQUEST} className="mt-2 text-sm text-brand-600 hover:underline">
            Iniciar nueva solicitud
          </Link>
        </div>
      ) : (
        <div>
          <h1 className="text-xl font-bold text-fg-primary">Confirmar reset de 2FA</h1>
          <p className="mt-3 text-sm text-fg-secondary">
            Al confirmar:
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-fg-secondary">
            <li>Se desactiva tu 2FA actual.</li>
            <li>Se borran los códigos de respaldo.</li>
            <li>Se cierran <strong>todas</strong> las sesiones activas.</li>
          </ul>
          <p className="mt-4 text-sm text-fg-secondary">
            Después podrás iniciar sesión con tu contraseña y reconfigurar 2FA cuando quieras.
          </p>
          <button
            type="button"
            onClick={onConfirm}
            disabled={state === 'loading'}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brand-500 to-rose-500 py-2.5 text-sm font-medium text-white transition-all hover:shadow-lg hover:shadow-brand-500/30 disabled:opacity-50"
          >
            {state === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Confirmar y desactivar 2FA
          </button>
        </div>
      )}
    </Shell>
  )
}

// ─── /mfa-recovery/cancel?token=… ─────────────────────────────────

export function MfaRecoveryCancelPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [state, setState] = useState<'loading' | 'ok' | 'err'>(token ? 'loading' : 'err')
  const [message, setMessage] = useState<string>(
    token ? '' : 'El enlace está incompleto.',
  )

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      try {
        const r = await mfaRecoveryApi.cancel(token)
        if (!cancelled) {
          setMessage(r.message)
          setState('ok')
        }
      } catch (err: unknown) {
        if (cancelled) return
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        setMessage(typeof detail === 'string' ? detail : 'No pudimos cancelar la solicitud.')
        setState('err')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  return (
    <Shell>
      {state === 'loading' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          <p className="text-sm text-fg-secondary">Cancelando solicitud…</p>
        </div>
      )}
      {state === 'ok' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle2 className="h-10 w-10 text-success-fg" />
          <h1 className="text-lg font-semibold text-fg-primary">Solicitud cancelada</h1>
          <p className="text-sm text-fg-secondary">{message}</p>
          <Link to={ROUTES.LOGIN} className="mt-2 text-sm text-brand-600 hover:underline">
            Ir al login
          </Link>
        </div>
      )}
      {state === 'err' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-10 w-10 text-error-fg" />
          <h1 className="text-lg font-semibold text-fg-primary">No pudimos cancelar</h1>
          <p className="text-sm text-fg-secondary">{message}</p>
        </div>
      )}
    </Shell>
  )
}
